import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useWebSocketEvent } from './use-websocket';

/**
 * Desktop + in-app notification bridge.
 *
 * The server fans out support-ticket activity (priority change, reassignment,
 * new comment) by persisting a notification row AND emitting a
 * `notification:new` WebSocket event to each recipient. This hook, mounted
 * once in the global Header, turns that event into:
 *
 *   1. An immediate refresh of the notification bell (list + unread badge),
 *      so it updates without waiting for the 15–30s poll.
 *   2. An OS-level desktop notification, when the user has granted
 *      permission.
 *
 * Permission is requested once, lazily, on mount. If the browser denies it
 * (or doesn't support the Notification API) the bell still updates — the
 * desktop toast is purely additive.
 */

interface IncomingNotification {
  id?: string;
  title?: string;
  body?: string;
  type?: string;
  entityType?: string | null;
  entityId?: string | null;
}

function desktopNotificationsSupported(): boolean {
  return typeof window !== 'undefined' && 'Notification' in window;
}

export function useDesktopNotifications(): void {
  const queryClient = useQueryClient();

  // Ask for OS permission once. Only prompt from the neutral "default"
  // state — never re-prompt after an explicit grant/deny.
  useEffect(() => {
    if (!desktopNotificationsSupported()) return;
    if (Notification.permission === 'default') {
      Notification.requestPermission().catch(() => {
        /* user dismissed — bell still works */
      });
    }
  }, []);

  useWebSocketEvent('notification:new', (data) => {
    const n = (data ?? {}) as IncomingNotification;

    // Keep the bell in sync immediately.
    queryClient.invalidateQueries({ queryKey: ['notifications'] });

    if (
      desktopNotificationsSupported() &&
      Notification.permission === 'granted'
    ) {
      try {
        const toast = new Notification(n.title || 'Notification', {
          body: n.body || '',
          // Dedupe repeat toasts for the same notification id.
          tag: n.id,
        });
        toast.onclick = () => {
          window.focus();
          toast.close();
        };
      } catch {
        // Some browsers throw when constructing a Notification outside a
        // service-worker context; the bell update above still happened.
      }
    }
  });
}
